import {
	useGetAreas,
	useGetParcellingAccessible,
	useGetProperties,
	useGetSectors,
	useGetSoilAnalyses,
} from '@practium/features/api';
import type { ParcellingMaturationCycle } from '@practium/features/parcelling';
import { formatDateFromISO } from '@practium/shared';
import type { FormSelectOption } from '@practium/ui';
import { DatePicker, FormField, FormInput, FormSectionTitle, FormSelect } from '@practium/ui';
import { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';
import {
	useGetAdviceCorrection,
	useGetAdviceCorrectionById,
} from '../../../../api/hooks/recommendations';
import { MATURATION_CYCLE_OPTIONS } from '../../../../parcelling/constants/maturationCycles.constants';
import {
	CROP_STAGE_OPTIONS,
	formatPeriodLabel,
	PARCELLING_TYPE_LABELS,
} from '../../../constants/maintenance.constants';
import type { UseMaintenanceFormOptions } from '../../../hooks/useMaintenanceForm';
import { useMaintenanceForm } from '../../../hooks/useMaintenanceForm';
import { useMaintenanceParcelling } from '../../../hooks/useMaintenanceParcelling';
import type {
	FertilizationPlanAdvice,
	MaintenanceParcellingType,
	RecommendationPageMode,
} from '../../../types';
import {
	getMaintenanceParcellingType,
	getMaintenanceReferenceId,
	getMaintenanceReferenceName,
	getMaintenanceSectorNames,
	normalizeMaintenanceMonth,
} from '../../../utils/maintenanceAdviceMapper';
import { MonthPicker } from '../../MonthPicker/MonthPicker';
import { MultiSelect } from '../../MultiSelect/MultiSelect';
import { ParcellingPreview } from '../../ParcellingPreview/ParcellingPreview';
import { ParcellingTypeSelector } from '../../ParcellingTypeSelector/ParcellingTypeSelector';
import styles from './MaintenanceForm.module.css';

const NONE_VALUE = 'none';

const maturationCycleLabel = (value: string) =>
	MATURATION_CYCLE_OPTIONS.find((option) => option.value === value)?.label ?? '';

export interface MaintenanceFormRef {
	submit: () => Promise<void>;
	isDirty: () => boolean;
}

export interface MaintenanceFormProps extends UseMaintenanceFormOptions {
	mode?: RecommendationPageMode;
	viewData?: FertilizationPlanAdvice;
	onLoadingChange?: (loading: boolean) => void;
	onPropertyChange?: (propertyId: string) => void;
	adviceId?: string;
}

const includeCurrentOption = (
	options: FormSelectOption[],
	value: FertilizationPlanAdvice['prop'] | null | undefined,
	fallbackLabel = ''
) => {
	const id = getMaintenanceReferenceId(value);
	if (!id || options.some((option) => option.value === id)) return options;
	return [
		{ value: id, label: getMaintenanceReferenceName(value, fallbackLabel) || id },
		...options,
	];
};

export const MaintenanceForm = forwardRef<MaintenanceFormRef, MaintenanceFormProps>(
	function MaintenanceForm(
		{
			mode = 'add',
			viewData,
			onLoadingChange,
			onPropertyChange,
			...hookProps
		},
		ref
	) {
		const isEdit = mode === 'edit';
		const isView = mode === 'view';
		const shouldLoadOptions = !isView && !isEdit;
		const plantingAdviceId = getMaintenanceReferenceId(viewData?.plantingAdvice);
		const embeddedPlantingAdviceName =
			viewData?.plantingAdviceName ||
			(viewData?.plantingAdvice && typeof viewData.plantingAdvice === 'object'
				? getMaintenanceReferenceName(viewData.plantingAdvice)
				: '');
		const { data: resolvedPlantingAdvice, isLoading: isLoadingPlantingAdvice } =
			useGetAdviceCorrectionById(plantingAdviceId, {
				enabled: !!plantingAdviceId && !embeddedPlantingAdviceName,
			});
		const resolvedPlantingAdviceName =
			embeddedPlantingAdviceName ||
			resolvedPlantingAdvice?.name ||
			(isLoadingPlantingAdvice ? 'Carregando...' : '');
		const { form, errors, isLoading, set, handleSubmit, isDirty, setParcellingContext } =
			useMaintenanceForm({
				...hookProps,
				validationMode: isEdit ? 'identification' : 'full',
			});
		const { data: properties = [] } = useGetProperties({ limit: 300, enabled: shouldLoadOptions });
		const { data: areas = [] } = useGetAreas({ limit: 300, enabled: shouldLoadOptions });
		const sectorsAreaId = isView ? getMaintenanceReferenceId(viewData?.area) : form.area;
		const { data: sectors = [] } = useGetSectors({
			limit: 300,
			areaId: sectorsAreaId || undefined,
			enabled: !!sectorsAreaId,
		});
		const { data: soilAnalyses = [] } = useGetSoilAnalyses({
			areaId: form.area,
			limit: 300,
			enabled: shouldLoadOptions && !!form.area,
		});
		const { data: correctionAdvices = [] } = useGetAdviceCorrection({
			limit: 300,
			areaId: form.area || undefined,
			enabled: shouldLoadOptions && !!form.area,
		});
		const selectedParcellingType = (form.parcellingType || undefined) as
			| MaintenanceParcellingType
			| undefined;
		const { data: parcellings = [] } = useGetParcellingAccessible({
			limit: 300,
			enabled: shouldLoadOptions,
			typeParcelling: selectedParcellingType ?? 'fixed',
		});
		const {
			balanceUnit,
			setBalanceUnit,
			previewRows,
			isLoadingPreview,
			previewEmptyMessage,
			showParcellingFields,
			showParcellingPreview,
			resetParcellingFields,
			setParcellingType,
			isDynamic,
			maturationCycle,
			setMaturationCycle,
			cycleCalendar,
		} = useMaintenanceParcelling({
			form,
			dynamicParcellings: parcellings,
			set,
			viewData,
			sectors,
			isView,
			isEdit,
			onContextChange: setParcellingContext,
		});
		const parcellingPreview = (
			<ParcellingPreview
				rows={previewRows}
				unit={balanceUnit}
				onUnitChange={setBalanceUnit}
				isLoading={isLoadingPreview}
				emptyMessage={previewEmptyMessage}
				calendar={cycleCalendar}
			/>
		);

		useImperativeHandle(ref, () => ({ submit: handleSubmit, isDirty }));

		useEffect(() => {
			onLoadingChange?.(isLoading);
		}, [isLoading, onLoadingChange]);

		useEffect(() => {
			onPropertyChange?.(form.prop);
		}, [form.prop, onPropertyChange]);

		const areaRecords = areas.filter((area) => area.propId === form.prop);
		const fetchedPropOptions = useMemo(
			() => properties.map((property) => ({ value: property._id, label: property.name })),
			[properties]
		);
		const fetchedAreaOptions = areaRecords.map((area) => ({
			value: area._id ?? '',
			label: area.name,
		}));
		const firstSelectedSector = sectors.find((sector) => sector._id === form.sectors[0]);
		const selectedSpacing = firstSelectedSector
			? `${firstSelectedSector.lineSpacing}x${firstSelectedSector.plantSpacing}`
			: '';
		const fetchedSectorOptions: FormSelectOption[] = sectors
			.filter((sector) => {
				const sectorAreaId =
					getMaintenanceReferenceId(sector.areaId) ||
					getMaintenanceReferenceId(
						(sector as { area?: FertilizationPlanAdvice['area'] }).area
					);
				return !sectorAreaId || sectorAreaId === form.area;
			})
			.map((sector) => ({
				value: sector._id,
				label: `${sector.name} (${sector.lineSpacing} x ${sector.plantSpacing})`,
				disabled:
					!!selectedSpacing && `${sector.lineSpacing}x${sector.plantSpacing}` !== selectedSpacing,
			}));
		const fetchedSoilAnalysisOptions = soilAnalyses.map((analysis) => ({
			value: analysis._id,
			label: analysis.name,
		}));
		const areaCorrectionOptions = correctionAdvices
			.filter((advice) => !form.area || getMaintenanceReferenceId(advice.area) === form.area)
			.map((advice) => ({ value: advice._id, label: advice.name }));
		const fetchedPlantingAdviceOptions = [
			{ value: NONE_VALUE, label: 'Não' },
			...areaCorrectionOptions,
		];
		const canSelectCorrection = !isEdit && !!form.area && areaCorrectionOptions.length > 0;
		const fetchedParcellingOptions = parcellings.map((parcelling) => ({
			value: parcelling._id,
			label: parcelling.name,
		}));
		const activePropOptions = includeCurrentOption(
			fetchedPropOptions,
			viewData?.prop,
			viewData?.propName
		);
		const activeAreaOptions = includeCurrentOption(
			fetchedAreaOptions,
			viewData?.area,
			viewData?.areaName
		);
		const activeSoilAnalysisOptions = includeCurrentOption(
			fetchedSoilAnalysisOptions,
			viewData?.soilAnalysis,
			viewData?.soilAnalysisName
		);
		const activePlantingAdviceOptions = includeCurrentOption(
			fetchedPlantingAdviceOptions,
			viewData?.plantingAdvice,
			resolvedPlantingAdviceName
		);
		const activeParcellingOptions = includeCurrentOption(
			fetchedParcellingOptions,
			viewData?.parcelling,
			viewData?.parcellingName
		);
		const activeSectorOptions = (viewData?.sectors ?? []).reduce<FormSelectOption[]>(
			(options, sector) => includeCurrentOption(options, sector),
			fetchedSectorOptions
		);
		if (isView && viewData) {
			const propName = getMaintenanceReferenceName(viewData.prop, viewData.propName);
			const areaName = getMaintenanceReferenceName(viewData.area, viewData.areaName);
			const sectorNames = getMaintenanceSectorNames(viewData.sectors);
			const soilAnalysisName = getMaintenanceReferenceName(
				viewData.soilAnalysis,
				viewData.soilAnalysisName
			);
			const plantingAdviceName = resolvedPlantingAdviceName;
			const parcellingName = getMaintenanceReferenceName(
				viewData.parcelling,
				viewData.parcellingName
			);
			const parcellingType = getMaintenanceParcellingType(viewData);
			const floweringMonth =
				normalizeMaintenanceMonth(viewData.floweringMonth) ||
				normalizeMaintenanceMonth(viewData.floweringDate);

			return (
				<div className={styles.form}>
					<div className={styles.section}>
						<FormSectionTitle>Identificação</FormSectionTitle>
						<div className={styles.sectionFields}>
							<div className={styles.row2}>
								<FormField label="Identificação">
									<span className={styles.viewText}>{viewData.name || '—'}</span>
								</FormField>
								<FormField label="Propriedade">
									<span className={styles.viewText}>{propName || '—'}</span>
								</FormField>
							</div>
							<div className={styles.row2}>
								<FormField label="Área">
									<span className={styles.viewText}>{areaName || '—'}</span>
								</FormField>
								<FormField label="Setores">
									<span className={styles.viewText}>{sectorNames.join(', ') || '—'}</span>
								</FormField>
							</div>
							<div className={styles.row2}>
								<FormField label="Análises de solo da área">
									<span className={styles.viewText}>{soilAnalysisName || '—'}</span>
								</FormField>
								<FormField label="Usar adubação de correção">
									<span className={styles.viewText}>
										{getMaintenanceReferenceId(viewData.plantingAdvice)
											? plantingAdviceName || 'Sim'
											: 'Não'}
									</span>
								</FormField>
							</div>
							<div className={styles.row2}>
								<FormField label="Fase da lavoura">
									<span className={styles.viewText}>
										{CROP_STAGE_OPTIONS.find((o) => o.value === viewData.cropStage)?.label ?? '—'}
									</span>
								</FormField>
								<FormField label="Produtividade esperada">
									<span className={styles.viewText}>{viewData.productivity ?? '—'}</span>
								</FormField>
							</div>
						</div>
					</div>

					<div className={styles.section}>
						<FormSectionTitle>Parcelamento</FormSectionTitle>
						<div className={styles.sectionFields}>
							<div className={styles.row2}>
								<FormField label="Tipo de parcelamento">
									<span className={styles.viewText}>
										{parcellingType ? PARCELLING_TYPE_LABELS[parcellingType] : '—'}
									</span>
								</FormField>
								{isDynamic ? (
									<FormField label="Data estimada da florada">
										<span className={styles.viewText}>
											{formatDateFromISO(viewData.floweringDate ?? '')}
										</span>
									</FormField>
								) : (
									<FormField label="Mês da florada">
										<span className={styles.viewText}>
											{formatPeriodLabel(floweringMonth) || '—'}
										</span>
									</FormField>
								)}
							</div>
							<div className={styles.row2}>
								{isDynamic ? (
									<FormField label="Ciclo de maturação">
										<span className={styles.viewText}>
											{maturationCycleLabel(maturationCycle) || '—'}
										</span>
									</FormField>
								) : (
									<FormField label="Esquema de parcelamento">
										<span className={styles.viewText}>{parcellingName || '—'}</span>
									</FormField>
								)}
							</div>
							{showParcellingPreview && parcellingPreview}
						</div>
					</div>
				</div>
			);
		}

		return (
			<div className={styles.form}>
				<div className={styles.section}>
					<FormSectionTitle>Identificação</FormSectionTitle>
					<div className={styles.sectionFields}>
						<div className={styles.row2}>
							<FormField label="Identificação" required error={errors.name}>
								<FormInput
									placeholder="Ex.: Recomendação José - 2025/2026"
									value={form.name}
									onChange={(e) => set('name', e.target.value)}
									hasError={!!errors.name}
								/>
							</FormField>
							<FormField label="Propriedade" required={!isEdit} error={errors.prop}>
								<FormSelect
									value={form.prop}
									onChange={(v) => {
										set('prop', v);
										set('area', '');
										set('soilAnalysis', '');
										set('sectors', []);
										set('plantingAdvice', NONE_VALUE);
										resetParcellingFields();
									}}
									options={activePropOptions}
									placeholder="Selecione a propriedade"
									hasError={!!errors.prop}
									disabled={isEdit}
								/>
							</FormField>
						</div>

						<div className={styles.row2}>
							<FormField label="Área" required={!isEdit} error={errors.area}>
								<FormSelect
									value={form.area}
									onChange={(v) => {
										set('area', v);
										set('soilAnalysis', '');
										set('sectors', []);
										set('plantingAdvice', NONE_VALUE);
										resetParcellingFields();
									}}
									options={activeAreaOptions}
									placeholder="Selecione a área"
									hasError={!!errors.area}
									disabled={isEdit}
								/>
							</FormField>
							<FormField label="Setores" required={!isEdit} error={errors.sectors}>
								<MultiSelect
									values={form.sectors}
									onChange={(v) => set('sectors', v)}
									options={activeSectorOptions}
									placeholder="Selecione os setores"
									hasError={!!errors.sectors}
									disabled={isEdit || !form.area}
								/>
							</FormField>
						</div>

						<div className={styles.row2}>
							<FormField
								label="Análises de solo da área"
								required={!isEdit}
								error={errors.soilAnalysis}
							>
								<FormSelect
									value={form.soilAnalysis}
									onChange={(v) => set('soilAnalysis', v)}
									options={activeSoilAnalysisOptions}
									placeholder="Selecione a análise"
									hasError={!!errors.soilAnalysis}
									disabled={isEdit}
								/>
							</FormField>
							<FormField
								label="Usar adubação de correção"
								required={!isEdit}
								error={errors.plantingAdvice}
							>
								<FormSelect
									value={form.plantingAdvice || NONE_VALUE}
									onChange={(v) => set('plantingAdvice', v)}
									options={activePlantingAdviceOptions}
									placeholder="Não"
									hasError={!!errors.plantingAdvice}
									disabled={!canSelectCorrection}
								/>
							</FormField>
						</div>

						<div className={styles.row2}>
							<FormField label="Fase da lavoura" required={!isEdit} error={errors.cropStage}>
								<FormSelect
									value={form.cropStage}
									onChange={(v) => {
										set('cropStage', v);
										resetParcellingFields();
										if (v === 'growth') set('productivity', '');
									}}
									options={CROP_STAGE_OPTIONS}
									placeholder="Selecione a fase"
									hasError={!!errors.cropStage}
									disabled={isEdit}
								/>
							</FormField>
							<FormField
								label="Produtividade esperada"
								required={!isEdit && form.cropStage !== 'growth'}
								error={errors.productivity}
							>
								<FormInput
									placeholder="Ex.: 70"
									type="number"
									min="0"
									step="5"
									suffix="sc/ha"
									value={form.productivity}
									onChange={(e) => set('productivity', e.target.value)}
									hasError={!!errors.productivity}
									disabled={isEdit || form.cropStage === 'growth'}
								/>
							</FormField>
						</div>
					</div>
				</div>

				<div className={styles.section}>
					<FormSectionTitle>Parcelamento</FormSectionTitle>
					<div className={styles.sectionFields}>
						<div className={styles.typeField}>
							<ParcellingTypeSelector
								value={form.parcellingType}
								onChange={setParcellingType}
								disabled={isEdit}
								hasError={!!errors.parcellingType}
							/>
							{errors.parcellingType && (
								<span className={styles.errorText}>{errors.parcellingType}</span>
							)}
						</div>
						{showParcellingFields && isDynamic && (
							<div className={`${styles.columns2} ${styles.reveal}`}>
								<FormField
									label="Data estimada da florada"
									required={!isEdit}
									error={errors.floweringDate}
								>
									<DatePicker
										value={form.floweringDate}
										onChange={(v) => set('floweringDate', v)}
										placeholder="Selecione a data da florada"
										hasError={!!errors.floweringDate}
										disabled={isEdit}
									/>
								</FormField>
								<FormField
									label="Ciclo de maturação"
									required={!isEdit}
									error={errors.maturationCycle}
								>
									<FormSelect
										value={maturationCycle}
										onChange={(v) => setMaturationCycle(v as ParcellingMaturationCycle)}
										options={MATURATION_CYCLE_OPTIONS}
										placeholder="Selecione o ciclo de maturação"
										hasError={!!errors.maturationCycle}
										disabled={isEdit}
									/>
								</FormField>
							</div>
						)}
						{showParcellingFields && !isDynamic && (
							<div className={`${styles.columns2} ${styles.reveal}`}>
								<FormField label="Mês da florada" required={!isEdit} error={errors.floweringMonth}>
									<MonthPicker
										value={form.floweringMonth}
										onChange={(v) => set('floweringMonth', v)}
										placeholder="Selecione o mês da florada"
										hasError={!!errors.floweringMonth}
										disabled={isEdit}
									/>
								</FormField>
								<FormField
									label="Esquema de parcelamento"
									required={!isEdit}
									error={errors.parcelling}
								>
									<FormSelect
										value={form.parcelling}
										onChange={(v) => set('parcelling', v)}
										options={activeParcellingOptions}
										placeholder="Selecione o esquema de parcelamento"
										hasError={!!errors.parcelling}
										disabled={isEdit}
									/>
								</FormField>
							</div>
						)}
						{showParcellingPreview && (
							<div className={styles.reveal}>{parcellingPreview}</div>
						)}
					</div>
				</div>
			</div>
		);
	}
);
